import { Component, Input, OnDestroy, OnInit, OnChanges, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  IOrganizationUnitCustomerDetailsEditable,
  OrganizationUnitCustomerDetailsService,
} from 'src/api/customer/organization-unit-customer-details/organization-unit-customer-details.service';
import { OrganizationUnitDetailsService } from 'src/api/customer/organization-unit-details/organization-unit-details.service';
import { IOrganizationUnitDetails } from 'typings/doenkids/doenkids';
import { ShowErrorsImmediatelyErrorMatcher } from 'src/components/shared/form-validators/error-state-matchers';
import { I18nToastProvider } from 'src/providers/i18n-toast.provider';

@Component({
    selector: 'app-organization-unit-customer-form',
    templateUrl: './organization-unit-customer-form.component.html',
    styleUrls: ['./organization-unit-customer-form.component.scss'],
    standalone: false
})
export class OrganizationUnitCustomerFormComponent implements OnInit, OnChanges, OnDestroy {
  private stop$ = new Subject<void>();

  @Input() organizationUnitId: number;

  @Input() inputAppearance: MatFormFieldAppearance = 'fill';

  @Input() isAdmin$: Observable<boolean>;

  @Input() hasCustomerWritePermission$: Observable<boolean>;

  @Input() isReadOnly: boolean = false;

  @Input() initialFormData: any;

  @Output() formChange = new EventEmitter<boolean>();

  @Output() saveRequested = new EventEmitter<void>();

  form: UntypedFormGroup;

  errorStateMatcher = new ShowErrorsImmediatelyErrorMatcher();

  useCustomerService = false;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private $organizationUnitCustomerDetailsService: OrganizationUnitCustomerDetailsService,
    private $organizationUnitDetailsService: OrganizationUnitDetailsService,
    private $i18nToastProvider: I18nToastProvider,
  ) {
    this.form = this.formBuilder.group({
      address: [''],
      postalcode: [''],
      city: [''],
      country: [''],
      email: [''],
      billing_address: [''],
      billing_postalcode: [''],
      billing_city: [''],
    });
  }

  async ngOnInit(): Promise<void> {
    await this.determineServiceToUse();
    this.setupFormChangeTracking();

    if (this.initialFormData) {
      this.form.patchValue(this.initialFormData);
    } else {
      await this.loadCustomerDetails();
    }

    if (this.isReadOnly) {
      this.form.disable();
    }
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes.organizationUnitId && !changes.organizationUnitId.firstChange) {
      await this.determineServiceToUse();
      await this.loadCustomerDetails();
    }
  }

  ngOnDestroy(): void {
    this.stop$.next();
    this.stop$.complete();
  }

  private async determineServiceToUse(): Promise<void> {
    const isAdmin = this.isAdmin$ ? await firstValueFrom(this.isAdmin$) : false;
    const hasCustomerWritePermission = this.hasCustomerWritePermission$ ? await firstValueFrom(this.hasCustomerWritePermission$) : false;

    this.useCustomerService = !isAdmin && hasCustomerWritePermission;
  }

  private setupFormChangeTracking(): void {
    this.form.valueChanges.pipe(
      takeUntil(this.stop$),
    ).subscribe(() => {
      this.formChange.emit(this.form.dirty);
    });
  }

  private async loadCustomerDetails(): Promise<void> {
    if (!this.organizationUnitId) {
      return;
    }

    try {
      if (this.useCustomerService) {
        const customerDetails = await this.$organizationUnitCustomerDetailsService.fetch(this.organizationUnitId);
        this.form.patchValue({
          address: customerDetails.address || '',
          postalcode: customerDetails.postalcode || '',
          city: customerDetails.city || '',
          email: customerDetails.email || '',
          billing_address: customerDetails.billing_address || '',
          billing_postalcode: customerDetails.billing_postalcode || '',
          billing_city: customerDetails.billing_city || '',
        });
      } else {
        // For admin users, load from organization details service
        const organizationDetails = await this.$organizationUnitDetailsService.fetch(this.organizationUnitId);
        this.form.patchValue({
          address: organizationDetails.address || '',
          postalcode: organizationDetails.postalcode || '',
          city: organizationDetails.city || '',
          country: organizationDetails.country_code || '',
          email: organizationDetails.email || '',
          billing_address: organizationDetails.billing_address || '',
          billing_postalcode: organizationDetails.billing_postalcode || '',
          billing_city: organizationDetails.billing_city || '',
        });
      }
    } catch (error) {
      this.$i18nToastProvider.error(_('organization_unit_details.customer_details.load.error'));
    }
  }

  async saveCustomerDetails(): Promise<boolean> {
    if (this.form.invalid) {
      return false;
    }

    try {
      const formValue = this.form.getRawValue();

      if (this.useCustomerService) {
        const customerDetailsUpdate: IOrganizationUnitCustomerDetailsEditable = {
          address: formValue.address,
          postalcode: formValue.postalcode,
          city: formValue.city,
          email: formValue.email,
          billing_address: formValue.billing_address,
          billing_postalcode: formValue.billing_postalcode,
          billing_city: formValue.billing_city,
        };

        await this.$organizationUnitCustomerDetailsService.update(this.organizationUnitId, customerDetailsUpdate);
      } else {
        const ouDetails: IOrganizationUnitDetails = {
          organization_unit_id: this.organizationUnitId,
          address: formValue.address,
          postalcode: formValue.postalcode,
          city: formValue.city,
          email: formValue.email,
          billing_address: formValue.billing_address,
          billing_postalcode: formValue.billing_postalcode,
          billing_city: formValue.billing_city,
        };

        await this.$organizationUnitDetailsService.update(ouDetails);
      }

      this.form.markAsPristine();
      this.$i18nToastProvider.success(_('organization_unit_details.customer_details.save.success'));
      return true;
    } catch (error) {
      this.$i18nToastProvider.error(_('organization_unit_details.customer_details.save.error'));
      return false;
    }
  }

  getFormValue() {
    return this.form.getRawValue();
  }

  isDirty(): boolean {
    return this.form.dirty;
  }

  isValid(): boolean {
    return this.form.valid;
  }

  markAsPristine(): void {
    this.form.markAsPristine();
  }

  patchValue(value: any): void {
    this.form.patchValue(value);
  }
}
